// React Imports
import { useState, useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

// Font Awesome Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faGripDotsVertical, faPencil, faPlusCircle, faTrash, faWarning, faExclamationTriangle, faDownload } from '@fortawesome/pro-light-svg-icons';

// Lodash Imports
import _, { min } from 'lodash';
import oxfordJoin from 'oxford-join';
import { v4 as uuidv4 } from 'uuid';

// MUI Imports
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

// Local imports
import ConfirmDialog from '../components/ConfirmDialog.js';
import SectionSettingsDialog from '../opportunities/OpportunityOneOffEmailBuilder.sectiondialog.js';
import OfferSettingsDialog from '../opportunities/OpportunityOneOffEmailBuilder.offerdialog.js';
import { allFieldsAssignedToSections } from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.utils.js';
import { Checkbox, FormControlLabel } from '@mui/material';

function StructurePane(props) {
    const [openFieldOfferSectionSelectorDialog, setOpenFieldOfferSectionSelectorDialog] = useState({});
    const theme = useTheme();

    const { organization, exchangeRates, noPreview = false, onClose, offers, setOffers, onPreview,
        onDownloadPdf, downloadingPdf, pdfError,
        exchangeRates: lockedExchangeRates, preface, buyingRates, products, attachPDF, setAttachPDF,
        validationErrors = [], errorFixMap = {} } = props;

    function _onPreview() {
        if (noPreview) {
            onClose();
        } else {
            onPreview();
        }
    }

    function setField({ field, offerId, sectionId }) {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);

            // Remove field from the current place it is in
            newOffers.forEach(offer => offer.sections.forEach(section => section.offers[0].fields = section.offers[0].fields.filter(f => f.id !== field.id)));

            // Add field to the new place
            const offer = newOffers.find(o => o.id === offerId);
            const section = offer.sections.find(s => s.id === sectionId);
            section.offers[0].fields.push(field);
            return newOffers;
        });
        setOpenFieldOfferSectionSelectorDialog({ ...openFieldOfferSectionSelectorDialog, open: false });
    }

    function onReorder({ sourceId, destinationOfferIndex, destinationSectionIndex, destinationFieldIndex }) {
        let sourceOfferIndex, sourceSectionIndex, sourceFieldIndex;
        
        // First try to find the field in all sections
        const found = _.find(offers, (o, oi) => o.sections.some((s, si) => s.offers[0].fields.some((f, fi) => {
            if (f.id === sourceId) {
                sourceOfferIndex = oi;
                sourceSectionIndex = si;
                sourceFieldIndex = fi;
                return true;
            }
            return false;
        })));

        // If not found, return early
        if (!found || _.isUndefined(sourceOfferIndex) || _.isUndefined(sourceSectionIndex) || _.isUndefined(sourceFieldIndex)) {
            return;
        }

        if (_.isUndefined(destinationOfferIndex) || _.isUndefined(destinationSectionIndex) || _.isUndefined(destinationFieldIndex)) {
            return;
        }

        const _offers = _.cloneDeep(offers);
        const source = _.cloneDeep(_offers[sourceOfferIndex].sections[sourceSectionIndex].offers[0].fields[sourceFieldIndex]);
        _offers[destinationOfferIndex].sections[destinationSectionIndex].offers[0].fields = 
            _offers[destinationOfferIndex].sections[destinationSectionIndex].offers[0].fields || [];
        _offers[sourceOfferIndex].sections[sourceSectionIndex].offers[0].fields.splice(sourceFieldIndex, 1, null);
        _offers[destinationOfferIndex].sections[destinationSectionIndex].offers[0].fields.splice(destinationFieldIndex, 0, source);
        _offers[sourceOfferIndex].sections[sourceSectionIndex].offers[0].fields =
            _.compact(_offers[sourceOfferIndex].sections[sourceSectionIndex].offers[0].fields);

        setOffers(_offers);
    }

    function onReorderSection({ sourceId, destinationOfferIndex, destinationSectionIndex }) {
        const [ sourceOfferIndex, sourceSectionIndex ] = (() => {
            let ret = null;
            _.find(offers, (o, oi) => o.sections.some((s, si) => {
                if (s.id === sourceId) {
                    ret = [oi, si];
                    return true;
                }
                return false;
            }));
            return ret || [];
        })();

        if (_.isUndefined(sourceOfferIndex) || _.isUndefined(sourceSectionIndex) || _.isUndefined(destinationOfferIndex) || _.isUndefined(destinationSectionIndex)) {
            return;
        }

        const _offers = _.cloneDeep(offers);
        const source = _.cloneDeep(_offers[sourceOfferIndex].sections[sourceSectionIndex]);
        _offers[destinationOfferIndex].sections = 
            _offers[destinationOfferIndex].sections || [];
        _offers[sourceOfferIndex].sections.splice(sourceSectionIndex, 1, null);
        _offers[destinationOfferIndex].sections.splice(destinationSectionIndex, 0, source);
        _offers[sourceOfferIndex].sections = _.compact(_offers[sourceOfferIndex].sections);

        setOffers(_offers);
    }

    function onReorderOffer({ sourceId, destinationOfferIndex }) {
        const sourceOfferIndex = _.findIndex(offers, o => o.id === sourceId);
        const _offers = _.cloneDeep(offers);
        const source = _.cloneDeep(_offers[sourceOfferIndex]);
        _offers.splice(sourceOfferIndex, 1, null);
        _offers.splice(destinationOfferIndex, 0, source);
        setOffers(_.compact(_offers));
    }

    // Extract all field IDs from preface
    const fieldIdsInPreface = preface ? _.uniq((preface.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g) || [])) : [];
    
    // Get all fields from all offers
    const allFieldsInOffers = _.flatMap(offers, o => 
        _.flatMap(o.sections, s => 
            _.flatMap(s.offers, so => so.fields)
        )
    );
    
    // Find fields that are in preface but not in any offer section
    const fieldsInPrefaceButNotInOffers = fieldIdsInPreface
        .map(id => allFieldsInOffers.find(f => f.id === id))
        .filter(Boolean)
        .filter(field => {
            // Check if field is already in a section
            return !offers.some(offer => 
                offer.sections.some(section => 
                    section.offers[0].fields.some(f => f.id === field.id)
                )
            );
        });
    
    // Remove fields that are not in preface anymore (only from the default unassigned section)
    useEffect(() => {
        if (!preface) return;
        // Match UUIDs referenced directly (id="<uuid>") or inside escaped JSON (e.g. data-total-fields="[&quot;<uuid>&quot;,...]")
        const currentFieldIds = _.uniq((preface.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g) || []));
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            let changed = false;
            newOffers.forEach(offer => {
                offer.sections.forEach(section => {
                    // Only auto-remove from the default unassigned section
                    if (section.title && section.title !== '***UNASSIGNED***') return;
                    const originalLength = section.offers[0].fields.length;
                    section.offers[0].fields = section.offers[0].fields.filter(f =>
                        currentFieldIds.includes(f.id)
                    );
                    if (section.offers[0].fields.length !== originalLength) {
                        changed = true;
                    }
                });
            });
            return changed ? newOffers : offers;
        });
    }, [preface, setOffers]);

    const offerFieldsInPreface = _.map(offers, o => _.filter(o.sections, s => _.filter(s.offers[0].fields, f => fieldIdsInPreface.includes(f.id))).flat()).flat();
    const offerFieldsUnassigned = _.map(offers, o => o.sections[0].offers[0].fields.filter(f => !offerFieldsInPreface.includes(f))).flat();

    return (
        <Box sx={{ margin: '0px 16px', width: 'calc(100% - 32px)', p: 1, border: `1px solid ${theme.palette.border1}` }}>
            {setAttachPDF && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
                    <FormControlLabel 
                        control={<Checkbox checked={attachPDF} onChange={e => setAttachPDF(e.target.checked)} />} 
                        label={<Typography sx={{ fontSize: '11px' }}>Include PDF in email</Typography>}
                    />
                    <Button
                        onClick={_onPreview}
                        sx={{
                            fontSize: '11px !important',
                            fontWeight: 400,
                            textTransform: 'uppercase',
                            color: '#3b82f6',
                            padding: '0px',
                            minWidth: 'auto',
                            '& .MuiButton-root': {
                                fontSize: '11px'
                            },
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                                color: '#2563eb'
                            }
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', fontWeight: 400, textTransform: 'uppercase' }}>
                            Preview
                        </Typography>
                    </Button>
                    {onDownloadPdf && (
                        <Tooltip title={pdfError || 'Download the PDF that will be attached'} arrow placement='top'>
                            {/* span keeps the tooltip working while the button is disabled */}
                            <span>
                                <IconButton
                                    size='small'
                                    onClick={onDownloadPdf}
                                    disabled={downloadingPdf}
                                    sx={{ padding: '2px', color: pdfError ? '#dc2626' : '#3b82f6', '&:hover': { backgroundColor: 'transparent', color: '#2563eb' } }}
                                >
                                    {downloadingPdf
                                        ? <CircularProgress size={12} />
                                        : <FontAwesomeIcon icon={faDownload} style={{ fontSize: '12px' }} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}

            {attachPDF && !allFieldsAssignedToSections({ offers, preface }) && (
                <Alert severity='warning' sx={{ mb: 1 }}>
                    Not all fields used in the quote have been assigned a section. <span style={{ fontWeight: '800', textDecoration: 'underline' }}>Quote sent might be wrong.</span>
                </Alert>
            )}
            <Paper sx={{ padding: '24px', border: `1px solid ${theme.palette.border1}`, opacity: attachPDF ? 1 : 0.5, pointerEvents: attachPDF ? 'auto' : 'none', backgroundColor: '#ffffff' }}>
                {offers.map((offer, index) => (
                    <Box key={offer.id} sx={{ 
                        border: '1px solid #f1f5f9', 
                        borderRadius: '8px', 
                        padding: '12px', 
                        marginBottom: '12px',
                        backgroundColor: '#ffffff'
                    }}>
                        <Offer
                            offer={offer}
                            offerIndex={index}
                            offers={offers}
                            setOffers={setOffers}
                            organization={organization}
                            exchangeRates={exchangeRates}
                            lockedExchangeRates={lockedExchangeRates}
                            preface={preface}
                            buyingRates={buyingRates}
                            products={products}
                            setOpenFieldOfferSectionSelectorDialog={setOpenFieldOfferSectionSelectorDialog}
                            onReorder={onReorder}
                            onReorderSection={onReorderSection}
                            onReorderOffer={onReorderOffer}
                            fieldIdsInPreface={fieldIdsInPreface}
                        />
                    </Box>
                    ))
                }
                {/* Show fields that are in preface but not assigned to any section */}
                {fieldsInPrefaceButNotInOffers.length > 0 && (
                    <Box sx={{ 
                        border: '1px solid #f1f5f9', 
                        borderRadius: '8px', 
                        padding: '12px', 
                        marginBottom: '12px',
                        backgroundColor: '#ffffff'
                    }}>
                        <Typography variant='body2' sx={{ mb: 1, color: grey[600], fontSize: '12px' }}>Unassigned Fields</Typography>
                        {fieldsInPrefaceButNotInOffers.map(field => (
                            <Box key={field.id} sx={{ marginLeft: '24px', marginBottom: '8px' }}>
                                <Field
                                    field={field}
                                    index={-1}
                                    offers={offers}
                                    setOffers={setOffers}
                                    buyingRates={buyingRates}
                                    products={products}
                                    onReorder={() => {}}
                                    unassigned={true}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
                <Button 
                        onClick={() => {
                            setOffers(offers => {
                                return [
                                    ...offers,
                                    {
                                        id: uuidv4(),
                                        title: 'New Offer',
                                        sections: ['***UNASSIGNED***', 'Origin', 'Freight', 'Destination', 'Inland', 'Additional Charges'].map(title => ({
                                            id: uuidv4(),
                                            title,
                                            description: '',
                                            offers: [{
                                                id: uuidv4(),
                                                fields: [],
                                            }]
                                        }))
                                    },
                                ];
                            });
                        }}
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            border: '1px dashed #3b82f6',
                            color: '#3b82f6',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: '#eff6ff',
                                border: '1px solid #3b82f6'
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faPlusCircle} />&nbsp;
                        Add Offer
                    </Button>
                {offerFieldsUnassigned.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant='caption' sx={{ display: 'block', mb: 1, color: grey[600] }}>
                            The following fields are not assigned to a section:
                        </Typography>
                        <Box sx={{ 
                            border: '1px solid #f1f5f9', 
                            borderRadius: '8px', 
                            padding: '12px', 
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            {offerFieldsUnassigned.map((field, index) => {
                                // Find which offer contains this field
                                const offerIndex = offers.findIndex(o => 
                                    o.sections[0] && o.sections[0].offers[0].fields.some(f => f.id === field.id)
                                );
                                const sectionIndex = 0; // Unassigned section is always index 0
                                
                                return (
                                    <Box key={field.id} sx={{ marginLeft: '0px' }}>
                                        <Field
                                            field={field}
                                            index={index}
                                            offers={offers}
                                            setOffers={setOffers}
                                            buyingRates={buyingRates}
                                            products={products}
                                            onReorder={({ sourceId, destination }) => {
                                                onReorder({ 
                                                    sourceId, 
                                                    destinationOfferIndex: offerIndex >= 0 ? offerIndex : 0, 
                                                    destinationSectionIndex: sectionIndex, 
                                                    destinationFieldIndex: destination 
                                                });
                                            }}
                                            unassigned={true}
                                            offerIndex={offerIndex >= 0 ? offerIndex : 0}
                                            sectionIndex={sectionIndex}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Paper>

            <FieldOfferSectionSelectorDialog
                open={openFieldOfferSectionSelectorDialog?.open}
                offers={offers}
                field={openFieldOfferSectionSelectorDialog?.field}
                buyingRates={buyingRates}
                products={products}
                setField={setField}
                onClose={() => setOpenFieldOfferSectionSelectorDialog({ ...openFieldOfferSectionSelectorDialog, open: false })}
            />
        </Box>
    );
}

function Offer(props) {
    const { offer, offerIndex, offers, setOffers, organization, exchangeRates, lockedExchangeRates, 
        preface, buyingRates, products, setOpenFieldOfferSectionSelectorDialog, onReorder, onReorderSection, onReorderOffer, fieldIdsInPreface } = props;
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleValue, setTitleValue] = useState(offer.title);
    const theme = useTheme();
    const ref = useRef(null);
    const dragHandleRef = useRef(null);
    const dropHandleRef = useRef(null);

    const [{ isDragging }, drag] = useDrag({
        type: `SidePaneStructure-Offer`,
        item: () => ({ id: offer.id, index: offerIndex }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver: isOverSection, canDrop: canDropSection }, dropSection] = useDrop({
        accept: `SidePaneStructure-Section`,
        drop: (item, monitor) => {
            onReorderSection({ sourceId: item.id, destinationOfferIndex: offerIndex, destinationSectionIndex: 1 });
        },
        canDrop: (item, monitor) => {
            return offer.sections?.length === 1 && offer.sections[0].title === '***UNASSIGNED***';
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: `SidePaneStructure-Offer`,
        drop: (item, monitor) => {
            if (!ref.current) return { index: offerIndex };
            ref.current.style.paddingTop = '0px'
            ref.current.style.paddingBottom = '0px'
            dropHandleRef.current.style.top = 'auto'
            dropHandleRef.current.style.bottom = 'auto';
            const rect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            const hoverY = clientOffset ? clientOffset.y - rect.top : 0;
            const midY = rect.height / 2;
            let calcIndex = offerIndex + (hoverY < midY ? 0 : 1);
            onReorderOffer({ sourceId: item.id, destinationOfferIndex: calcIndex });
        },
        hover(item, monitor) {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            const hoverY = monitor.getClientOffset().y - rect.top;
            const midY = rect.height / 2;
        
            if (hoverY < midY) {
                ref.current.style.paddingTop = '30px'
                ref.current.style.paddingBottom = '0px'
                dropHandleRef.current.style.top = '0px';
                dropHandleRef.current.style.bottom = 'auto';
            } else { 
                ref.current.style.paddingTop = '0px'
                ref.current.style.paddingBottom = '30px'
                dropHandleRef.current.style.top = 'auto'
                dropHandleRef.current.style.bottom = '0px';
            };
        },
        end: (item, monitor) => {
            ref.current.style.paddingTop = '0px'
            ref.current.style.paddingBottom = '0px'
            dropHandleRef.current.style.top = 'auto'
            dropHandleRef.current.style.bottom = 'auto';
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });


    const offset = offers[offerIndex].sections[0].title === '***UNASSIGNED***' ? 1 : 0;

    const [deleteOfferOpen, setDeleteOfferOpen] = useState(false);
    const [openOfferSettingsDialog, setOpenOfferSettingsDialog] = useState(false);

    function onDeleteOffer() {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers.splice(offerIndex, 1);
            return newOffers;
        });
        setDeleteOfferOpen(false);
    }

    function setOffer({ offer }) {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers[offerIndex] = offer;
            return newOffers;
        });
    }

    const offerFieldsInPreface = _.map(offer.sections, s => _.filter(s.offers[0].fields, f => preface.includes(f.id))).flat();
    const isHovering = isOver && canDrop && !isDragging;
    const isHoveringSection = isOverSection && canDropSection && !isDragging;
    dropSection(ref);
    drop(ref);
    drag(dragHandleRef);

    return (
        <Box sx={{ position: 'relative', paddingLeft: '0px', paddingBottom: isHovering ? '' : '0px !important', paddingTop: isHovering ? '' : '0px !important' }} ref={ref}>
            <Typography variant='caption' component={Box} sx={{ position: 'absolute', left: '0px', width: '100%', top: '0px', height: '30px', border: '1px dashed #333', display: isHovering ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }} ref={dropHandleRef}>
                Drop here
            </Typography>
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    width: '100%', 
                    position: isHovering ? 'relative' : 'static',
                    '&:hover .offer-delete-button': {
                        opacity: 1
                    }
                }} 
                ref={dragHandleRef}
            >
                <div
                    style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: '12px', marginTop: '4px', color: '#64748b' }}
                >
                    <FontAwesomeIcon icon={faGripDotsVertical} style={{ fontSize: '14px' }} />
                </div>
                {editingTitle ? (
                    <TextField
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        onBlur={() => {
                            setOffers(offers => {
                                const newOffers = _.cloneDeep(offers);
                                newOffers[offerIndex].title = titleValue || '';
                                return newOffers;
                            });
                            setTitleValue(titleValue || '');
                            setEditingTitle(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            } else if (e.key === 'Escape') {
                                setTitleValue(offer.title || '');
                                setEditingTitle(false);
                            }
                        }}
                        autoFocus
                        variant='standard'
                        placeholder='Enter offer title'
                        sx={{ 
                            flex: 1,
                            '& .MuiInput-input': {
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '0px'
                            },
                            '& .MuiInput-input::placeholder': {
                                opacity: 0.5,
                                color: grey[500]
                            }
                        }}
                    />
                ) : (
                    <Typography 
                        variant='body1' 
                        sx={{ 
                            flex: 1, 
                            fontSize: '14px', 
                            fontWeight: 500, 
                            cursor: 'text',
                            minHeight: '20px',
                            color: offer.title ? 'inherit' : grey[500],
                            fontStyle: offer.title ? 'normal' : 'italic'
                        }}
                        onClick={() => {
                            setTitleValue(offer.title || '');
                            setEditingTitle(true);
                        }}
                    >
                        {offer.title || 'Click to add title'}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {!_.isEmpty(offerFieldsInPreface) && (
                        <Tooltip title={'The following fields from this offer are still used in the email: ' + oxfordJoin(_.map(offerFieldsInPreface, f => f.title))} slotProps={{ popper: { sx: { zIndex: 10000000050 } } }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '13px', margin: '0px 4px', color: grey[500], cursor: 'pointer' }} />
                        </Tooltip>
                    )}
                    {_.isEmpty(offerFieldsInPreface) && (
                        <IconButton
                            size='small'
                            color='error'
                            onClick={() => {
                                setDeleteOfferOpen(true);
                            }}
                            className='offer-delete-button'
                            sx={{ 
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '& .MuiSvgIcon-root': { fontSize: '14px' }
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                {offer.transitTime && (
                    <Typography
                        component='div'
                        variant='caption'>
                        <b>Transit Time:</b> {
                            (() => {
                                const v = offer.transitTime;
                                if (!v) return null;
                                if (_.isString(v)) return v;
                                const mins = parseInt((v % 1) * 60);
                                const days = parseInt(parseInt(v) / 24);
                                const hours = parseInt(v) % 24;
                                return [
                                    days ? (days === 1 ? `${days} day` : `${days} days`) : '',
                                    hours ? (hours === 1 ? `${hours} hour` : `${hours} hours`) : '',
                                    mins ? (mins === 1 ? `${mins} min` : `${mins} mins`) : '',
                                ].join(' ');
                            })()
                        }</Typography>
                )}
                {offer.validUntil && (
                    <Typography component='div' variant='caption'><b>Valid Until:</b> {offer.validUntil}</Typography>
                )}
            </Box>
            {isHoveringSection && (
                <Box sx={{ height: '30px', border: '1px dashed #333', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', marginLeft: '24px', width: 'calc(100% - 24px)' }}>
                    <Typography variant='caption'>Drop here</Typography>
                </Box>
            )}
            {offer.sections.filter(s => s.title !== '***UNASSIGNED***').map((section, index) => (
                <Box key={section.id} sx={{ 
                    border: '1px solid #f1f5f9', 
                    borderRadius: '6px', 
                    padding: '10px', 
                    marginTop: '8px',
                    marginLeft: '24px',
                    backgroundColor: '#ffffff'
                }}>
                    <Section
                        section={section}
                        sectionIndex={index + offset}
                        offer={offer}
                        offerIndex={offerIndex}
                        offers={offers}
                        setOffers={setOffers}
                        organization={organization}
                        exchangeRates={exchangeRates}
                        lockedExchangeRates={lockedExchangeRates}
                        buyingRates={buyingRates}
                        products={products}
                        onReorder={onReorder}
                        onReorderSection={onReorderSection}
                        fieldIdsInPreface={fieldIdsInPreface}
                    />
                </Box>
            ))}
            <Button 
                sx={{ 
                    marginLeft: '24px',
                    marginTop: '8px',
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    border: '1px dashed #3b82f6',
                    color: '#3b82f6',
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: '#eff6ff',
                        border: '1px solid #3b82f6'
                    }
                }}
                onClick={() => {
                    setOffers(offers => {
                        const newOffers = _.cloneDeep(offers);
                        newOffers[offerIndex].sections.push({
                            id: uuidv4(),
                            title: 'New Section',
                            offers: [{ id: uuidv4(), fields: [] }],
                        });
                        return newOffers;
                    });
            }}>
                <FontAwesomeIcon icon={faPlusCircle} />&nbsp;
                Add Section
            </Button>

            <ConfirmDialog
                open={deleteOfferOpen}
                onClose={() => setDeleteOfferOpen(false)}
                onConfirm={onDeleteOffer}
                color='error'
                sx={{ zIndex: 1000050 }}
            >
                Are you sure you want to delete offer '{offer.title}'?
            </ConfirmDialog>

            <OfferSettingsDialog
                open={openOfferSettingsDialog}
                onClose={() => setOpenOfferSettingsDialog(false)}
                offer={offer}
                setOffer={setOffer}
                onDeleteOffer={_.isEmpty(offerFieldsInPreface) ? onDeleteOffer : null}
                organization={organization}
                exchangeRates={exchangeRates}
                lockedExchangeRates={lockedExchangeRates}
                sx={{ zIndex: 1000050 }}
            />
        </Box>
    );
}

function Section(props) {
    const { section, sectionIndex, offer, offerIndex, offers, setOffers, organization, exchangeRates, 
        lockedExchangeRates, buyingRates, products, onReorder, onReorderSection, fieldIdsInPreface } = props;
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleValue, setTitleValue] = useState(section.title);

    const ref = useRef(null);
    const dragHandleRef = useRef(null);
    const dropHandleRef = useRef(null);

    const [{ isDragging }, drag] = useDrag({
        type: `SidePaneStructure-Section`,
        item: () => ({ id: section.id, index: sectionIndex }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver: isOverField, canDrop: canDropField }, dropField] = useDrop({
        accept: `SidePaneStructure-Field`,
        drop: (item, monitor) => {
            // Calculate the destination index - append to end of fields array
            const destinationFieldIndex = section.offers[0].fields.length;
            onReorder({ sourceId: item.id, destinationOfferIndex: offerIndex, destinationSectionIndex: sectionIndex, destinationFieldIndex });
        },
        canDrop: (item, monitor) => {
            // Allow dropping if section title is not UNASSIGNED
            return section.title !== '***UNASSIGNED***';
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: `SidePaneStructure-Section`,
        drop: (item, monitor) => {
            if (!ref.current) return { index: sectionIndex };
            ref.current.style.paddingTop = '0px'
            ref.current.style.paddingBottom = '0px'
            dropHandleRef.current.style.top = 'auto'
            dropHandleRef.current.style.bottom = 'auto';
            const rect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            const hoverY = clientOffset ? clientOffset.y - rect.top : 0;
            const midY = rect.height / 2;
            let calcIndex = sectionIndex + (hoverY < midY ? 0 : 1);
            onReorderSection({ sourceId: item.id, destinationOfferIndex: offerIndex, destinationSectionIndex: calcIndex });
        },
        hover(item, monitor) {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            const hoverY = monitor.getClientOffset().y - rect.top;
            const midY = rect.height / 2;
        
            if (hoverY < midY) {
                ref.current.style.paddingTop = '30px'
                ref.current.style.paddingBottom = '0px'
                dropHandleRef.current.style.top = '0px';
                dropHandleRef.current.style.bottom = 'auto';
            } else { 
                ref.current.style.paddingTop = '0px'
                ref.current.style.paddingBottom = '30px'
                dropHandleRef.current.style.top = 'auto'
                dropHandleRef.current.style.bottom = '0px';
            };
        },
        end: (item, monitor) => {
            ref.current.style.paddingTop = '0px'
            ref.current.style.paddingBottom = '0px'
            dropHandleRef.current.style.top = 'auto'
            dropHandleRef.current.style.bottom = 'auto';
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    dropField(ref);
    drop(ref);
    drag(dragHandleRef);

    const [deleteSectionOpen, setDeleteSectionOpen] = useState(false);
    const [openSectionSettingsDialog, setOpenSectionSettingsDialog] = useState(false);

    function onDeleteSection() {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers[offerIndex].sections[0].offers[0].fields = [
                ...(newOffers[offerIndex].sections[0].offers[0].fields || []),
                ...(newOffers[offerIndex].sections[sectionIndex].offers[0].fields || [])
            ];
            newOffers[offerIndex].sections = newOffers[offerIndex].sections.filter(s => s.id !== section.id);
            return newOffers;
        });
    }

    function setSection({ section }) {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers[offerIndex].sections[sectionIndex] = section;
            return newOffers;
        });
    }

    const isHoveringField = isOverField && canDropField && !isDragging;
    const isHovering = isOver && canDrop && !isDragging;

    return (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '0px', paddingBottom: isHovering ? '' : '0px !important', paddingTop: isHovering ? '' : '0px !important' }} ref={ref}>
            <Typography variant='caption' component={Box} sx={{ position: 'absolute', left: '0px', width: '100%', top: '0px', height: '30px', border: '1px dashed #333', display: isHovering ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }} ref={dropHandleRef}>
                Drop here
            </Typography>
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    width: '100%', 
                    position: isHovering ? 'relative' : 'static',
                    '&:hover .section-delete-button': {
                        opacity: 1
                    }
                }} 
                ref={dragHandleRef}
            >
                <div
                    style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: '12px', marginTop: '4px', color: '#64748b' }}
                >
                    <FontAwesomeIcon icon={faGripDotsVertical} style={{ fontSize: '14px' }} />
                </div>
                {editingTitle ? (
                    <TextField
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        onBlur={() => {
                            setOffers(offers => {
                                const newOffers = _.cloneDeep(offers);
                                newOffers[offerIndex].sections[sectionIndex].title = titleValue || '';
                                return newOffers;
                            });
                            setTitleValue(titleValue || '');
                            setEditingTitle(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            } else if (e.key === 'Escape') {
                                setTitleValue(section.title || '');
                                setEditingTitle(false);
                            }
                        }}
                        autoFocus
                        variant='standard'
                        placeholder='Enter section name'
                        sx={{ 
                            flex: 1,
                            '& .MuiInput-input': {
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '0px'
                            },
                            '& .MuiInput-input::placeholder': {
                                opacity: 0.5,
                                color: grey[500]
                            }
                        }}
                    />
                ) : (
                    <Typography 
                        variant='body1' 
                        sx={{ 
                            flex: 1, 
                            fontSize: '14px', 
                            fontWeight: 500, 
                            cursor: 'text',
                            minHeight: '20px',
                            color: section.title ? 'inherit' : grey[500],
                            fontStyle: section.title ? 'normal' : 'italic'
                        }}
                        onClick={() => {
                            setTitleValue(section.title || '');
                            setEditingTitle(true);
                        }}
                    >
                        {section.title || 'Click to add section name'}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <IconButton
                        size='small'
                        color='error'
                        onClick={() => {
                            setDeleteSectionOpen(true);
                        }}
                        className='section-delete-button'
                        sx={{ 
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '& .MuiSvgIcon-root': { fontSize: '14px' }
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                </Box>
            </Box>
            {isHoveringField && (
                <Box sx={{ height: '30px', border: '1px dashed #333', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', marginLeft: '24px', width: 'calc(100% - 24px)' }}>
                    <Typography variant='caption'>Drop here</Typography>
                </Box>
            )}
            {section.offers[0].fields.map((field, index) => (
                <Box key={field.id} sx={{ marginLeft: '24px' }}>
                    <Field
                        field={field}
                        index={index}
                        section={section}
                        sectionIndex={sectionIndex}
                        offer={offer}
                        offerIndex={offerIndex}
                        offers={offers}
                        setOffers={setOffers}
                        buyingRates={buyingRates}
                        products={products}
                        onReorder={({ sourceId, destination }) => {
                            onReorder({ sourceId, destinationOfferIndex: offerIndex, destinationSectionIndex: sectionIndex, destinationFieldIndex: destination });
                        }}
                    />
                </Box>
            ))}

            <SectionSettingsDialog
                open={openSectionSettingsDialog}
                onClose={() => setOpenSectionSettingsDialog(false)}
                section={section}
                setSection={setSection}
                onDeleteSection={() => {
                    setOpenSectionSettingsDialog(false);
                    onDeleteSection()
                }}
                organization={organization}
                exchangeRates={exchangeRates}
                lockedExchangeRates={lockedExchangeRates}
                sx={{ zIndex: 10000000050 }}
            />

            <ConfirmDialog
                open={deleteSectionOpen}
                onClose={() => setDeleteSectionOpen(false)}
                onConfirm={onDeleteSection}
                color='error'
                sx={{ zIndex: 10000000050 }}
            >
                Are you sure you want to delete section '{section.title}'?
            </ConfirmDialog>
        </Box>
    );
}

function Field(props) {
    const { field, index, offers, setOffers, buyingRates, products, onReorder, unassigned, section, offerIndex, sectionIndex } = props;
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleValue, setTitleValue] = useState(field.title);
    const [deleteFieldOpen, setDeleteFieldOpen] = useState(false);

    const dropHandleRef = useRef(null);
    const dragHandleRef = useRef(null);
    const ref = useRef(null);

    const [{ isDragging }, drag] = useDrag({
        type: `SidePaneStructure-Field`,
        item: () => ({ id: field.id, index }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: `SidePaneStructure-Field`,
        drop: (item, monitor) => {
            if (!ref.current) return { index };
            const rect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            const hoverY = clientOffset ? clientOffset.y - rect.top : 0;
            const midY = rect.height / 2;
            let calcIndex = index + (hoverY < midY ? 0 : 1);
            onReorder({ sourceId: item.id, destination: calcIndex });
        },
        hover(item, monitor) {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            const hoverY = monitor.getClientOffset().y - rect.top;
            const midY = rect.height / 2;
        
            if (hoverY < midY) {
                dragHandleRef.current.style.top = '30px';
                dropHandleRef.current.style.top = '0px';
                dropHandleRef.current.style.bottom = 'auto';
            } else { 
                dragHandleRef.current.style.top = '0px';
                dropHandleRef.current.style.top = 'auto'
                dropHandleRef.current.style.bottom = '0px';
            };
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    drag(dragHandleRef);
    drop(ref);

    const isHovering = isOver && canDrop && !isDragging;

    const [openFieldOfferSectionSelectorDialog, setOpenFieldOfferSectionSelectorDialog] = useState({});

    function setField({ field, offerId, sectionId }) {
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);

            // Remove field from the current place it is in
            newOffers.forEach(offer => offer.sections.forEach(section => section.offers[0].fields = section.offers[0].fields.filter(f => f.id !== field.id)));

            // Add field to the new place
            const offer = newOffers.find(o => o.id === offerId);
            const section = offer.sections.find(s => s.id === sectionId);
            section.offers[0].fields.push(field);
            return newOffers;
        });
        setOpenFieldOfferSectionSelectorDialog({ ...openFieldOfferSectionSelectorDialog, open: false });
    }

    function onDeleteField() {
        if (unassigned) {
            // For unassigned fields, we can't delete them from offers, but we can remove them from the display
            // The parent component will handle this via the useEffect that removes fields not in preface
            setDeleteFieldOpen(false);
            return;
        }
        setOffers(offers => {
            const newOffers = _.cloneDeep(offers);
            newOffers[offerIndex].sections[sectionIndex].offers[0].fields = 
                newOffers[offerIndex].sections[sectionIndex].offers[0].fields.filter(f => f.id !== field.id);
            return newOffers;
        });
        setDeleteFieldOpen(false);
    }

    const subtitle = (() => {
        const parts = [];
        if (!_.isEmpty(field.buyingRates)) {
            const rates = field.buyingRates.map(br => buyingRates.find(b => b.id === br.rateId));
            parts.push(`${oxfordJoin(_.uniq(_.map(rates, r => `${r.supplier.organization} - ${r.product.type}`)))}`);
        }
        if (_.isEmpty(field.buyingRates) && (field.type === 'per-unit-type')) {
            let productIds = _.keys(field.values).filter(k => field.values[k].value || field.values[k].formula);
            productIds = productIds.filter(id => products.find(p => p.id === id));
            parts.push(`Priced for ${oxfordJoin(_.map(productIds, id => products.find(p => p.id === id).type))}`);
        }

        return _.isEmpty(parts) ? null : parts.join(' | ');
    })();

    return (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '4px', paddingBottom: isHovering ? '30px' : '0px', paddingLeft: '0px' }} ref={ref}>
            <Typography variant='caption' component={Box} sx={{ position: 'absolute', left: '0px', width: '100%', top: '0px', height: '30px', border: '1px dashed #333', display: isHovering ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', zIndex: 1 }} ref={dropHandleRef}>
                Drop here
            </Typography>
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    width: '100%', 
                    position: isHovering ? 'relative' : 'static',
                    '&:hover .field-delete-button': {
                        opacity: 1
                    }
                }} 
                ref={dragHandleRef}
            >
                <div
                    style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: '12px', marginTop: '4px', color: '#64748b' }}
                >
                    <FontAwesomeIcon icon={faGripDotsVertical} style={{ fontSize: '14px' }} />
                </div>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
                        {editingTitle ? (
                            <TextField
                                value={titleValue}
                                onChange={(e) => setTitleValue(e.target.value)}
                                onBlur={() => {
                                    setOffers(offers => {
                                        const newOffers = _.cloneDeep(offers);
                                        if (unassigned) {
                                            // Find field in unassigned section
                                            const allFields = _.flatMap(newOffers, o => 
                                                _.flatMap(o.sections, s => 
                                                    _.flatMap(s.offers, so => so.fields)
                                                )
                                            );
                                            const fieldToUpdate = allFields.find(f => f.id === field.id);
                                            if (fieldToUpdate) {
                                                fieldToUpdate.title = titleValue || '';
                                            }
                                        } else {
                                            const fieldToUpdate = newOffers[offerIndex].sections[sectionIndex].offers[0].fields.find(f => f.id === field.id);
                                            if (fieldToUpdate) {
                                                fieldToUpdate.title = titleValue || '';
                                            }
                                        }
                                        return newOffers;
                                    });
                                    setTitleValue(titleValue || '');
                                    setEditingTitle(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    } else if (e.key === 'Escape') {
                                        setTitleValue(field.title || '');
                                        setEditingTitle(false);
                                    }
                                }}
                                autoFocus
                                variant='standard'
                                placeholder='Enter field name'
                                sx={{ 
                                    '& .MuiInput-input': {
                                        fontSize: '14px',
                                        padding: '0px'
                                    },
                                    '& .MuiInput-input::placeholder': {
                                        opacity: 0.5,
                                        color: grey[500]
                                    }
                                }}
                            />
                        ) : (
                            <Typography 
                                variant='body1' 
                                sx={{ 
                                    fontSize: '14px', 
                                    cursor: 'text',
                                    minHeight: '20px',
                                    color: field.title ? 'inherit' : grey[500],
                                    fontStyle: field.title ? 'normal' : 'italic'
                                }}
                                onClick={() => {
                                    setTitleValue(field.title || '');
                                    setEditingTitle(true);
                                }}
                            >
                                {field.title || 'Click to add field name'}
                            </Typography>
                        )}
                        {subtitle && <Typography variant='caption' sx={{ color: '#64748b', fontSize: '12px' }}>{subtitle}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {!unassigned && (
                            <IconButton
                                size='small'
                                color='error'
                                onClick={() => setDeleteFieldOpen(true)}
                                className='field-delete-button'
                                sx={{ 
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    '& .MuiSvgIcon-root': { fontSize: '14px' }
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </IconButton>
                        )}
                    </Box>
                    <FieldOfferSectionSelectorDialog
                        open={openFieldOfferSectionSelectorDialog?.open}
                        offers={offers}
                        field={openFieldOfferSectionSelectorDialog?.field}
                        setField={setField}
                        onClose={() => setOpenFieldOfferSectionSelectorDialog({ ...openFieldOfferSectionSelectorDialog, open: false })}
                        buyingRates={buyingRates}
                        products={products}
                    />
                </Box>
            </Box>
            {!unassigned && (
                <ConfirmDialog
                    open={deleteFieldOpen}
                    onClose={() => setDeleteFieldOpen(false)}
                    onConfirm={onDeleteField}
                    color='error'
                    sx={{ zIndex: 10000000050 }}
                >
                    Are you sure you want to delete field '{field.title}'?
                </ConfirmDialog>
            )}
        </Box >
    );
}

export default StructurePane;

function DraggableList(props) {
    const { items, renderItem, onReorder } = props;
    const listId = useRef(`list-${Math.random().toString(36).substr(2, 9)}`);

    return (
        <div>
            {items?.map((item, index) => (
                <DraggableListItem
                    key={item.id}
                    item={item}
                    index={index}
                    listId={listId.current}
                    renderItem={renderItem}
                    onReorder={onReorder}
                />
            ))}
        </div>
    );
}

function DraggableListItem(props) {
    const { item, index, listId, renderItem, onReorder } = props;
    const ref = useRef(null);
    const dragHandleRef = useRef(null);
    const theme = useTheme();

    const [{ isDragging }, drag] = useDrag({
        type: `DraggableList-${listId}`,
        item: () => ({ id: item.id, index }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (draggedItem, monitor) => {
            const dropResult = monitor.getDropResult();
            if (dropResult && dropResult.index !== undefined && dropResult.index !== draggedItem.index) {
                onReorder({ source: draggedItem.index, destination: dropResult.index });
            }
        },
    });

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: `DraggableList-${listId}`,
        drop: () => ({ index }),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    drag(dragHandleRef);
    drop(ref);

    const isHovering = isOver && canDrop && !isDragging;

    return (
        <>
            <div
                ref={ref}
                style={{
                    opacity: isDragging ? 0.5 : 1,
                }}
            >
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'start', 
                        gap: '12px',
                    }}
                >
                    <div 
                        ref={dragHandleRef}
                        style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
                    >
                        <FontAwesomeIcon icon={faGripDotsVertical} />
                    </div>
                    {renderItem(item, index)}
                </Box>
            </div>
            {isHovering && (
                <Box
                    sx={{
                        height: '6px',
                        backgroundColor: theme.palette.primary.main,
                        margin: '4px 0px',
                        borderRadius: '3px',
                        transition: 'all 0.2s ease',
                        width: '100%',
                    }}
                />
            )}
        </>
    );
}

function FieldOfferSectionSelectorDialog(props) {
    const { open, offers, field, setField, onClose, buyingRates, products } = props;

    function findOffer() {
        if (!field) return null;
        return _.find(offers, o => o.sections.some(s => s.offers[0].fields.some(f => f.id === field.id)));
    }

    function findSection() {
        if (!field) return null;
        const offer = findOffer();
        if (!offer) return null;
        return offer.sections.find(s => s.offers[0].fields.some(f => f.id === field.id));
    }

    const [offerId, setOfferId] = useState(findOffer()?.id);
    const [sectionId, setSectionId] = useState(findSection()?.id);

    useEffect(() => {
        setOfferId(findOffer()?.id);
        setSectionId(findSection()?.id);
    }, [field, open]);

    const subtitle = (() => {
        if (!field) return null;
        const parts = [];
        if (!_.isEmpty(field.buyingRates)) {
            const rates = field.buyingRates.map(br => buyingRates.find(b => b.id === br.rateId));
            parts.push(`${oxfordJoin(_.uniq(_.map(rates, r => `${r.supplier.organization} - ${r.product.type}`)))}`);
        }
        if (_.isEmpty(field.buyingRates) && (field.type === 'per-unit-type')) {
            const productIds = _.keys(field.values).filter(k => field.values[k].value || field.values[k].formula);
            parts.push(`Priced for ${oxfordJoin(_.map(productIds, id => products.find(p => p.id === id)?.type))}`);
        }

        return _.isEmpty(parts) ? null : parts.join(' | ');
    })();

    return (
        <Dialog open={open} onClose={onClose} sx={{ zIndex: 10000000050 }}>
            <DialogTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <Typography variant='body1' sx={{ fontWeight: 800 }}>{field?.title}</Typography>
                    {subtitle && <Typography variant='caption' sx={{ color: 'text.secondary' }}>{subtitle}</Typography>}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Typography variant='body1'>Select an offer or section to assign this field to:</Typography>
                    <Select
                        value={offerId || '-'}
                        onChange={(e) => setOfferId(e.target.value)}
                        MenuProps={{
                            style: {
                                zIndex: 10000000051,
                            },
                        }}
                    >
                        <MenuItem value='-' disabled>-</MenuItem>
                        {offers.map(offer => (
                            <MenuItem key={offer.id} value={offer.id}>{offer.title}</MenuItem>
                        ))}
                    </Select>
                    <Select
                        disabled={!offerId}
                        value={sectionId || '-'}
                        onChange={(e) => setSectionId(e.target.value)}
                        MenuProps={{
                            style: {
                                zIndex: 10000000051,
                            },
                        }}
                    >
                        <MenuItem value='-' disabled>-</MenuItem>
                        {offers.find(o => o.id === offerId)?.sections.map(section => (
                            <MenuItem key={section.id} value={section.id}>{section.title === '***UNASSIGNED***' ? 'Unassigned' : section.title}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined' color='primary'>Cancel</Button>
                <Button onClick={() => setField({ field, offerId, sectionId })} variant='contained' color='primary' sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    )
}

function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
}